import { Fragment } from 'react';
import { useInView } from 'react-intersection-observer';

import { noop } from 'lodash';

import { Modal, ModalCloseButton, ModalContent, ModalHeading, ModalTrigger, Spinner } from '@uikit';

import { DeserializedAppData } from '@api';

import { ReviewContentReviewData } from '@shared/api/types/review';

import ReviewContent from '@store:web/components/common/ReviewContent/ReviewContent';

export type BaseReviewListModalProps = {
  title?: string;
  reviews: ReviewContentReviewData[] | ReviewContentReviewData[][];
  isLoading?: boolean;
  isLoadingMore?: boolean;
  showReviewFlag?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  app: DeserializedAppData;
};

const ReviewListModal = ({
  title,
  isLoading,
  onLoadMore = noop,
  hasMore,
  reviews,
  isLoadingMore,
  app,
  showReviewFlag,
}: BaseReviewListModalProps) => {
  const { ref } = useInView({
    onChange: (inView) => inView && !isLoading && !isLoadingMore && onLoadMore(),
  });

  return (
    <Modal>
      <ModalTrigger
        className="whitespace-nowrap text-secondary-700 font-medium text-sm"
        data-qa="view-all-button"
      >
        View all ››
      </ModalTrigger>
      <ModalContent className="flex flex-col gap-8 px-5 py-10 md:px-10 w-full h-full max-h-[640px] max-w-xl overflow-hidden">
        <ModalCloseButton className="absolute right-4 top-4" />
        {title && (
          <header>
            <ModalHeading className="m-0">{title}</ModalHeading>
          </header>
        )}

        {!isLoading && !reviews?.length && (
          <p className="mt-10 text-center text-neutral-200 text-sm">Empty</p>
        )}

        {!isLoading && !!reviews.length && (
          <ul className="flex flex-col overflow-y-scroll gap-5 pr-5 md:pr-10 -mr-5 md:-mr-10 -mb-10 pb-10">
            {reviews.map((review, i) => {
              if (Array.isArray(review)) {
                return (
                  <Fragment key={i}>
                    {review.map((singleReview) => {
                      return (
                        <li key={singleReview.id}>
                          <ReviewContent
                            app={app.attributes}
                            reviewTitleTag="h3"
                            className="rounded-xl border border-primary-100 p-5"
                            review={singleReview}
                            showReviewFlag={showReviewFlag}
                            isFeedbackHorizontal
                          />
                        </li>
                      );
                    })}
                  </Fragment>
                );
              }

              return (
                <li key={review.id}>
                  <ReviewContent
                    app={app.attributes}
                    className="rounded-xl border border-primary-100 p-5"
                    review={review}
                    data-qa="review-popup-item"
                    showReviewFlag={showReviewFlag}
                    isFeedbackHorizontal
                  />
                </li>
              );
            })}

            {(isLoading || hasMore) && (
              // Key is added to trigger in view cb again if component is already in view
              <li className="self-center justify-self-center mt-5">
                <Spinner key={reviews.length} ref={ref} size="md" />
              </li>
            )}
          </ul>
        )}
      </ModalContent>
    </Modal>
  );
};

export default ReviewListModal;
